import { HStack, IconButton } from '@chakra-ui/react'
import { Panel } from '@xyflow/react'
import type { FC } from 'react'
import { useMemo } from 'react'
import { memo } from 'react'

import AutoLayoutButton from './nav/autoLayoutButton'
import ButtonWrapper from './nav/buttonWrapper'

import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@app/components/ui/menu'
import AnalyzeVariance from '@app/pages/strategies/components/map/analyzeVariance'
import CopyMapToOrganizationButton from '@app/pages/strategies/components/map/copyMapToOrganizationButton'
import MapDateFilters from '@app/pages/strategies/components/map/filters/dateFilters/mapDateFilters'
import MapSegmentFilters from '@app/pages/strategies/components/map/filters/segmentFilters/mapSegmentFilters'
import MapExpandAllNodes from '@app/pages/strategies/components/map/mapExpandAllNodes'
import MapStatDisplay from '@app/pages/strategies/components/map/mapStatDisplay'
import MapSimulationPopulateBaselines from '@app/pages/strategies/components/map/nav/mapSimulationPopulateBaselines'
import MapSimulationRun from '@app/pages/strategies/components/map/nav/mapSimulationRun'
import MapSimulationToggle from '@app/pages/strategies/components/map/nav/mapSimulationToggle'
import RunStats from '@app/pages/strategies/components/map/runStats'
import { useStrategyMapContext } from '@app/pages/strategies/strategyMapContext'
import Can from '@app/shared/authorization/can'
import IconMap from '@app/utils/iconMap'

type Props = {
  strategyId: string
}

const MapFiltering: FC<Props> = ({ strategyId }) => {
  const { canEdit, mapMode } = useStrategyMapContext()
  const simulationMode = mapMode === 'simulation'

  const extraOptionsMenu = useMemo(
    () => (
      <MenuRoot closeOnSelect={false}>
        <MenuTrigger asChild>
          <IconButton variant="ghost" size="2xs" bgColor="bg.subtle">
            <IconMap.Ellipsis />
          </IconButton>
        </MenuTrigger>
        <MenuContent>
          <MenuItem value="analyze-variance">
            <AnalyzeVariance strategyId={strategyId} />
          </MenuItem>
          <MenuItem value="relayout">
            <AutoLayoutButton strategyId={strategyId} />
          </MenuItem>
          <Can I="view" an="admin">
            <MenuItem value="copy-to-doubleloop">
              <CopyMapToOrganizationButton strategyId={strategyId} />
            </MenuItem>
          </Can>
        </MenuContent>
      </MenuRoot>
    ),
    [strategyId]
  )

  return (
    <Panel position="bottom-right">
      <ButtonWrapper>
        <HStack px={1} py={1}>
          {!simulationMode && (
            <>
              <MapSegmentFilters strategyId={strategyId} canEdit={canEdit} />
              <MapExpandAllNodes />
              <MapStatDisplay strategyId={strategyId} canEdit={canEdit} />
              <MapDateFilters strategyId={strategyId} canEdit={canEdit} />
              <RunStats strategyId={strategyId} />
            </>
          )}

          {canEdit && (
            <>
              <MapSimulationToggle />
              {simulationMode && (
                <>
                  <MapSimulationRun strategyId={strategyId} size="xs" py={0} px={1} h="inherit" />
                  <MapSimulationPopulateBaselines
                    strategyId={strategyId}
                    variant="outline"
                    size="xs"
                    py={0}
                    px={1}
                    h="inherit"
                  />
                </>
              )}
            </>
          )}
          {extraOptionsMenu}
        </HStack>
      </ButtonWrapper>
    </Panel>
  )
}

MapFiltering.displayName = 'MapFiltering'

export default memo(MapFiltering)
